import React from 'react';
import clsx from 'clsx';
import { Component } from '@headless-workspace/typings';
import { cva } from 'class-variance-authority';
import { PropsWithTestId } from '../../props';
import { FieldProps, FieldState, GlowFieldWrapper } from '../fieldWrapper';
import style from './glowTextArea.module.css';

type GlowTextAreaProps = React.ComponentPropsWithRef<'textarea'> &
    FieldProps &
    PropsWithTestId & {
        forceState?: FieldState;
        id: string;
    };

const borderClasses = cva('rounded-0.5 outline focus:outline-border-selected focus:outline-bold', {
    variants: {
        state: {
            default: 'outline-border-primary outline-medium',
            error: 'outline-border-error outline-bold',
        },
    },
    defaultVariants: {
        state: 'default',
    },
});

export const GlowTextArea: Component<GlowTextAreaProps> = ({
    className,
    dataTestId,
    defaultValue,
    errorText,
    label,
    helperText,
    forceState,
    optionalText,
    displayMaxLength,
    ...props
}) => {
    return (
        <GlowFieldWrapper
            label={label}
            helperText={helperText}
            errorText={errorText}
            dataTestId={dataTestId}
            value={props.value}
            optionalText={optionalText}
            forceState={forceState}
            fieldId={props.id}
            maxLength={props.maxLength}
            required={props.required}
            displayMaxLength={displayMaxLength}
        >
            {({ state, helperTextId, errorTextId, ...fieldProps }) => {
                const ariaDescribedBy = [helperTextId, errorTextId].join(' ').trim();

                return (
                    <textarea
                        className={clsx(
                            'text-text-primary font-brand',
                            'h-[7.75rem]',
                            'p-0.75',
                            'bg-background-form',
                            'resize-none',
                            borderClasses({ state }),
                            className,
                            style.textarea,
                        )}
                        {...props}
                        {...fieldProps}
                        data-testid={dataTestId}
                        defaultValue={defaultValue}
                        aria-describedby={ariaDescribedBy}
                        maxLength={props.maxLength}
                        aria-invalid={state === 'error'}
                        id={props.id}
                    />
                );
            }}
        </GlowFieldWrapper>
    );
};
